"use client";

import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Button } from "../button";
import { Input } from "../input";
import { Label } from "../label";
import { Textarea } from "../textarea";
import { Switch } from "../switch";
import { DatePickerField } from "../unifiedtime/DatePickerField";
import { TimePickerField } from "../unifiedtime/TimePickerField";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../hooks/useAuth";
import { useCreateGoogleEvent, useCreateMicrosoftEvent } from "../../hooks/use-api";
import type { BookingData } from "../../types/calendar";

interface AdvancedBookingFormProps {
  onSubmit?: (data: BookingData) => Promise<void>;
  isLoading?: boolean;
  selectedSlot?: string | null;
  customerName?: string;
  platform?: "google" | "microsoft" | "both";
  bookeeEmail?: string;
  selectedCalendar?: string;
}

export const AdvancedBookingForm = ({
  onSubmit,
  isLoading = false,
  selectedSlot,
  customerName,
  platform = "both",
  bookeeEmail,
  selectedCalendar = "",
}: AdvancedBookingFormProps) => {
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  const createGoogleEvent = useCreateGoogleEvent();
  const createMicrosoftEvent = useCreateMicrosoftEvent();
  const [formData, setFormData] = useState<Partial<BookingData>>({
    email: "",
    description: "",
    location: "",
    meetingType: "inPerson",
    bookeeEmail: bookeeEmail || "",
    slot: {
      start: "",
      end: "",
    },
  });

  // Update bookeeEmail when prop changes
  useEffect(() => {
    if (bookeeEmail) {
      setFormData(prev => ({ ...prev, bookeeEmail }));
    }
  }, [bookeeEmail]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [isOnline, setIsOnline] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<
    "google" | "microsoft"
  >("google");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedDate || !startTime || !endTime) {
      toast({
        title: "Missing Information",
        description: "Please select a date and time for your meeting.",
        variant: "destructive",
      });

      return;
    }

    if (startTime >= endTime) {
      toast({
        title: "Invalid Time Range",
        description: "End time must be after start time.",
        variant: "destructive",
      });

      return;
    }

    try {
      const bookingData: BookingData = {
        email: formData.email || "",
        description: formData.description || "",
        location: formData.location || "",
        meetingType: formData.meetingType || "inPerson",
        bookeeEmail: formData.bookeeEmail || "",
        slot: {
          start: format(startTime, "yyyy-MM-dd'T'HH:mm:ss"),
          end: format(endTime, "yyyy-MM-dd'T'HH:mm:ss"),
        },
        address: formData.address,
        otherDetails: formData.otherDetails,
      };

      // Create event based on selected platform
      const eventData = {
        summary: formData.description || "Meeting",
        description: formData.description,
        location: formData.location,
        start: {
          dateTime: format(startTime, "yyyy-MM-dd'T'HH:mm:ss"),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: format(endTime, "yyyy-MM-dd'T'HH:mm:ss"),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        attendees: [], // Will be handled by the booking API
        isOnline,
      };

      // Determine platform based on selected calendar
      const targetPlatform = selectedCalendar === "microsoft" ? "microsoft" : "google";
      
      if (targetPlatform === "google") {
        await createGoogleEvent.mutateAsync(eventData);
      } else {
        await createMicrosoftEvent.mutateAsync(eventData);
      }

      // Reset form
      setFormData({
        email: "",
        description: "",
        location: "",
        meetingType: "inPerson",
        bookeeEmail: "",
        slot: {
          start: "",
          end: "",
        },
      });
      setSelectedDate(null);
      setStartTime(null);
      setEndTime(null);

      // Toast is handled by the mutation hooks

      if (onSubmit) {
        await onSubmit(bookingData);
      }
    } catch (error) {
      console.error("Booking failed:", error);
      toast({
        title: "Booking Failed",
        description:
          "There was an error scheduling your meeting. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isFormValid =
    formData.email &&
    formData.bookeeEmail &&
    formData.description &&
    selectedDate &&
    startTime &&
    endTime &&
    !isLoading;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Advanced Booking</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Your Email *</Label>
            <Input
              id="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="bookeeEmail">Bookee Email *</Label>
            <Input
              id="bookeeEmail"
              value={formData.bookeeEmail}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  bookeeEmail: e.target.value,
                }))
              }
              placeholder="person.to.book@example.com"
              required
              readOnly={!!bookeeEmail}
              className={bookeeEmail ? "bg-card-50" : ""}
            />
            {bookeeEmail && (
              <p className="text-xs text-gray-500 mt-1">
                This email was selected from the availability check above.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Meeting Description *</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder={
                customerName
                  ? `Meeting with ${customerName}`
                  : "Enter meeting description"
              }
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Enter meeting description"
              rows={3}
            />
          </div>

          <DatePickerField
            label="Meeting Date *"
            selected={selectedDate}
            onChange={setSelectedDate}
            placeholder="Select meeting date"
            minDate={new Date()}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TimePickerField
              label="Start Time *"
              selected={startTime}
              onChange={setStartTime}
              placeholder="Select start time"
              timeIntervals={15}
            />
            <TimePickerField
              label="End Time *"
              selected={endTime}
              onChange={setEndTime}
              placeholder="Select end time"
              timeIntervals={15}
            />
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, location: e.target.value }))
              }
              placeholder={isOnline ? "Video call link" : "Meeting location"}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="online"
              checked={isOnline}
              onCheckedChange={setIsOnline}
            />
            <Label htmlFor="online">Online Meeting</Label>
          </div>

          <div>
            <Label htmlFor="address">Address (Optional)</Label>
            <Input
              id="address"
              value={formData.address || ""}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, address: e.target.value }))
              }
              placeholder="Meeting address"
            />
          </div>

          <div>
            <Label htmlFor="otherDetails">Additional Details (Optional)</Label>
            <Textarea
              id="otherDetails"
              value={formData.otherDetails || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  otherDetails: e.target.value,
                }))
              }
              placeholder="Any additional information about the meeting"
              rows={3}
            />
          </div>

          {platform === "both" && (
            <div className="space-y-2">
              <Label>Calendar Platform</Label>
              <div className="flex space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="google"
                    name="platform"
                    value="google"
                    checked={selectedPlatform === "google"}
                    onChange={(e) =>
                      setSelectedPlatform(
                        e.target.value as "google" | "microsoft",
                      )
                    }
                  />
                  <Label htmlFor="google">Google Calendar</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="microsoft"
                    name="platform"
                    value="microsoft"
                    checked={selectedPlatform === "microsoft"}
                    onChange={(e) =>
                      setSelectedPlatform(
                        e.target.value as "google" | "microsoft",
                      )
                    }
                  />
                  <Label htmlFor="microsoft">Microsoft Calendar</Label>
                </div>
              </div>
            </div>
          )}

          {selectedSlot && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                Selected slot: {selectedSlot}
              </p>
            </div>
          )}

          <Button type="submit" disabled={!isFormValid} className="w-full">
            {isLoading
              ? "Booking..."
              : `Create ${selectedPlatform === "google" ? "Google" : "Microsoft"} Meeting`}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
